/**
 * iter213b — Garage Hive Deep-Link Gateway page.
 *
 * Entry point that Garage Hive AL extensions call via
 *   /gh/open?mode=<foh|technician>&<context params>
 *
 * Reads validated query params, forwards them to the backend
 * `resolve-deep-link` endpoint, then follows the returned action:
 *
 *   * `open_intake`         → navigate to the returned destination_url
 *   * `garage_hive_search`  → navigate to the returned destination_url
 *                             (StartIntake with GH search pre-populated)
 *   * `error`               → show a controlled error + recovery link
 *
 * Authentication:
 *   * If the user is not logged in, we redirect to /app/login with a
 *     `location.state.from` containing the current full URL. The login
 *     page already honours `location.state.from` for return-after-login.
 *   * We DO NOT accept `returnTo` as a URL query param — that would be
 *     an open-redirect vulnerability. All post-resolution navigation
 *     uses internal Yestimate routes returned by the backend.
 */
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Loader2, AlertCircle, Cable, ArrowRight, Search,
} from "lucide-react";
import { useAuth, API } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";

// Only forward known parameters to the backend. Everything else is
// dropped silently so unexpected/oversized query strings can't
// destabilise the resolver.
const ALLOWED_PARAMS = [
  "mode",
  "jobsheet_id",
  "jobsheet_no",
  "vehicle_id",
  "registration",
  "reg",
  "customer_id",
  "customer_no",
  "customer_name",
];

// Never trust internal routes coming from anywhere but the backend
// response. This whitelist tags what we accept as a Yestimate route
// prefix, guarding against a compromised response accidentally
// redirecting elsewhere.
const SAFE_INTERNAL_PREFIXES = ["/app/", "/gh/"];

function isSafeInternal(url) {
  if (!url || typeof url !== "string") return false;
  if (!url.startsWith("/")) return false;         // block absolute externals
  if (url.startsWith("//")) return false;         // block protocol-relative
  return SAFE_INTERNAL_PREFIXES.some(p => url.startsWith(p));
}

export default function GHOpenGateway() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  const [phase, setPhase] = useState("boot");     // boot|resolving|error
  const [detail, setDetail] = useState("");
  const [errorInfo, setErrorInfo] = useState(null);

  useEffect(() => {
    // Wait for the auth provider to finish restoring the session from
    // localStorage before deciding whether we're authenticated.
    if (authLoading) return;

    const token = localStorage.getItem("token");

    // Not authenticated → bounce to login, preserving full URL as
    // `location.state.from` so the return-after-login flow brings the
    // user straight back here.
    if (!user || !token) {
      const returnUrl = `${location.pathname}${location.search}`;
      navigate("/app/login", { replace: true, state: { from: returnUrl } });
      return;
    }

    // Parse query params → build a clean allow-listed payload.
    const params = new URLSearchParams(location.search);
    const payload = {};
    for (const key of ALLOWED_PARAMS) {
      const v = params.get(key);
      if (v != null && v !== "") payload[key] = v;
    }

    // Describe what we're resolving so the loading screen isn't silent.
    if (payload.jobsheet_no || payload.jobsheet_id) {
      setDetail(`Opening Garage Hive jobsheet ${payload.jobsheet_no || "…"}`);
    } else if (payload.registration || payload.reg) {
      setDetail(`Finding jobsheets for ${(payload.registration || payload.reg).toUpperCase()}`);
    } else if (payload.customer_name || payload.customer_no) {
      setDetail(`Finding jobsheets for ${payload.customer_name || payload.customer_no}`);
    } else {
      setDetail("Opening Garage Hive search");
    }
    setPhase("resolving");

    axios.post(
      `${API}/foh/integrations/garage-hive/resolve-deep-link`,
      payload,
      { headers: { Authorization: `Bearer ${token}` } },
    ).then((r) => {
      const data = r.data || {};
      const action = data.action;
      const dest = data.destination_url;

      if ((action === "open_intake" || action === "garage_hive_search") && isSafeInternal(dest)) {
        navigate(dest, { replace: true });
        return;
      }
      // Backend returned a controlled error, or the destination
      // failed the internal-route whitelist — show recovery UI.
      setErrorInfo({
        action,
        message: data.error || (
          action === "error"
            ? "We couldn't resolve this Garage Hive record."
            : "That Garage Hive link didn't resolve to a Yestimate destination."
        ),
        error_code: data.error_code,
        warnings: data.warnings || [],
        fallback_url: isSafeInternal(dest) ? dest : "/app/foh/start-intake?ghSearch=1",
      });
      setPhase("error");
    }).catch((e) => {
      const detail = e?.response?.data?.detail;
      setErrorInfo({
        action: "error",
        message: typeof detail === "string" ? detail
                 : (e?.message || "The Garage Hive deep-link couldn't be resolved."),
        error_code: e?.response?.status ? `http_${e.response.status}` : "network_error",
        warnings: [],
        fallback_url: "/app/foh/start-intake?ghSearch=1",
      });
      setPhase("error");
    });
  }, [user, authLoading]);

  // ------ Render ---------------------------------------------------

  if (phase === "boot" || phase === "resolving") {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center px-6" data-testid="gh-open-loading">
        <div className="w-16 h-16 rounded-full bg-teal-100 border border-teal-200 flex items-center justify-center shadow-sm mb-5">
          <Cable className="w-7 h-7 text-teal-700" />
        </div>
        <div className="flex items-center gap-2 text-lg font-bold text-slate-900">
          <Loader2 className="w-5 h-5 animate-spin text-teal-700" />
          Opening Yestimate…
        </div>
        <div className="mt-1 text-sm text-slate-500" data-testid="gh-open-detail">{detail}</div>
      </div>
    );
  }

  // Error state
  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-6" data-testid="gh-open-error">
      <div className="max-w-md w-full bg-white border border-red-200 rounded-xl shadow-sm p-6">
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-full bg-red-50 border border-red-200 shrink-0">
            <AlertCircle className="w-5 h-5 text-red-600" />
          </div>
          <div className="min-w-0">
            <h1 className="text-lg font-bold text-slate-900">
              We couldn&apos;t open this Garage Hive record
            </h1>
            <p className="mt-1 text-sm text-slate-600 leading-snug">
              {errorInfo?.message}
            </p>
            {errorInfo?.warnings && errorInfo.warnings.length > 0 && (
              <ul className="mt-2 space-y-0.5 text-xs text-slate-500 list-disc pl-4">
                {errorInfo.warnings.map((w, i) => <li key={i}>{w}</li>)}
              </ul>
            )}
          </div>
        </div>

        <div className="mt-5 flex flex-col gap-2">
          <Button
            onClick={() => navigate(errorInfo?.fallback_url || "/app/foh/start-intake?ghSearch=1", { replace: true })}
            className="w-full"
            data-testid="gh-open-fallback-btn"
          >
            <Search className="w-4 h-4 mr-2" />
            Search Garage Hive instead
          </Button>
          <Button
            variant="outline"
            onClick={() => navigate("/app/foh", { replace: true })}
            className="w-full"
            data-testid="gh-open-home-btn"
          >
            Go to FOH dashboard <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>

        {errorInfo?.error_code && (
          <p className="mt-4 text-[10px] uppercase tracking-wider text-slate-400 font-mono text-right"
             data-testid="gh-open-error-code">
            {errorInfo.error_code}
          </p>
        )}
      </div>
    </div>
  );
}
